import { useState, useMemo, useEffect } from 'react';
import { Plus, UserPlus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { palette, shadow, transition, admin } from '../styles/tokens';
import { useAuthStore } from '../stores/authStore';
import { useTeam } from '../hooks/useTeam';
import { useResolvedClusterConfig } from '../hooks/useResolvedClusterConfig';
import { supportsCommissions } from '../data/commissionConfig';
import { MetricCard } from '../components/common/MetricCard';
import { TeamRoster } from '../components/team/TeamRoster';
import { HoursPayroll } from '../components/team/HoursPayroll';
import { CertificationsTab } from '../components/team/CertificationsTab';
import { LeaveRequestsTab } from '../components/team/LeaveRequestsTab';
import { CommissionsTab } from '../components/team/CommissionsTab';
import { AddToTeamPanel } from '../components/team/AddToTeamPanel';
import { EditTeamMemberPanel } from '../components/team/EditTeamMemberPanel';
import { Toast } from '../components/common/Toast';
import { Skeleton, TableSkeleton } from '../components/common/Skeleton';
import { TeamInvitesTab } from '../components/team/TeamInvitesTab';
import { ActivityLogTab } from '../components/team/ActivityLogTab';
import type { TeamMember } from '../types';

type TeamTab = 'roster' | 'payroll' | 'certifications' | 'leave' | 'invite_codes' | 'activity' | 'commissions';

const BASE_TABS: { key: TeamTab; label: string }[] = [
  { key: 'roster', label: 'Team Roster' },
  { key: 'payroll', label: 'Hours & Payroll' },
  { key: 'certifications', label: 'Certifications' },
  { key: 'leave', label: 'Leave Requests' },
  { key: 'invite_codes', label: 'Invites' },
  { key: 'activity', label: 'Activity Log' },
];

export function TeamPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const { config: clusterConfig } = useResolvedClusterConfig(business?.industry ?? null);
  const showCommissions = supportsCommissions(clusterConfig.clusterKey);
  const TABS = useMemo(() => showCommissions
    ? [...BASE_TABS.slice(0, 2), { key: 'commissions' as TeamTab, label: 'Commissions' }, ...BASE_TABS.slice(2)]
    : BASE_TABS, [showCommissions]);
  const [tab, setTab] = useState<TeamTab>('roster');
  const [memberPanel, setMemberPanel] = useState(false);
  const [editMember, setEditMember] = useState<TeamMember | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const otThreshold = business?.business_rules?.overtime_threshold_hours ?? 44;
  const otWarningThreshold = business?.ot_warning_threshold ?? 36;

  const [searchParams, setSearchParams] = useSearchParams();
  const { teamMembers, certAlerts, leaveRequests, loading, error, refresh } = useTeam(business?.id, otThreshold);

  useEffect(() => { document.title = 'CrewBit · Team & Payroll'; }, []);

  useEffect(() => {
    const action = searchParams.get('action');
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot URL-to-state sync: reads ?action= on mount, then clears it
    if (action === 'add-member' || action === 'add-to-team') { setMemberPanel(true); setSearchParams({}); }
  }, [searchParams, setSearchParams]);

  // Summary metrics
  const activeCount = teamMembers.filter(m => m.status === 'active').length;
  const employeeCount = teamMembers.filter(m => m.employmentType === 'employee' && m.status === 'active').length;
  const contractorCount = teamMembers.filter(m => m.employmentType === 'contractor' && m.status === 'active').length;
  const operatorCount = teamMembers.filter(m => m.employmentType === 'operator' && m.status === 'active').length;

  const totalHours = useMemo(() =>
    teamMembers.reduce((sum, m) => sum + m.hoursThisPeriod, 0),
  [teamMembers]);
  const otMembers = teamMembers.filter(m => m.overtimeHours > 0).length;

  const pendingLeave = leaveRequests.filter(r => r.status === 'pending').length;
  const pendingActions = pendingLeave + certAlerts.filter(c => c.status === 'expired' || c.status === 'expiring').length + otMembers;

  const typeSub = [
    employeeCount > 0 ? `${employeeCount} emp` : '',
    contractorCount > 0 ? `${contractorCount} cont` : '',
    operatorCount > 0 ? `${operatorCount} op` : '',
  ].filter(Boolean).join(' · ');

  if (loading) {
    return (
      <div>
        <Skeleton width={180} height={24} style={{ marginBottom: 24 }} />
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[1, 2, 3].map(i => <div key={i} style={{ flex: 1, padding: '18px 20px', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}><Skeleton width={80} height={10} style={{ marginBottom: 8 }} /><Skeleton width={100} height={24} /></div>)}
        </div>
        <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: '20px' }}><TableSkeleton rows={6} columns={7} /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Team & Payroll</div>
        <div style={{ padding: '20px', background: palette.redWash, borderRadius: admin.cardRadius, borderLeft: `4px solid ${palette.red}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div><div style={{ fontSize: 14, fontWeight: 600, color: admin.header }}>Failed to load team data</div><div style={{ fontSize: 13, color: palette.g500, marginTop: 2 }}>{error}</div></div>
          <button onClick={refresh} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: palette.red, color: palette.white, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Try again</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Team & Payroll</div>
          <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Manage your team, track hours, and run payroll</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setTab('invite_codes'); }} style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
            border: `1px solid ${palette.accent}`, background: palette.white, fontSize: 13, fontWeight: 600,
            color: palette.accent, cursor: 'pointer', fontFamily: 'inherit',
          }}><UserPlus size={15} /> Invite Member</button>
          <button onClick={() => setMemberPanel(true)} style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8,
            border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600,
            color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
          }}><Plus size={15} /> Add to Team</button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard
          label="Active team"
          value={String(activeCount)}
          sub={typeSub}
        />
        <MetricCard
          label="Hours this week"
          value={`${totalHours.toFixed(1)}h`}
          delta={otMembers > 0 ? `${otMembers} in OT` : undefined}
          deltaType={otMembers > 0 ? 'warn' : undefined}
        />
        <MetricCard
          label="Pending actions"
          value={String(pendingActions)}
          sub={[
            pendingLeave > 0 ? `${pendingLeave} leave` : '',
            certAlerts.length > 0 ? `${certAlerts.length} certs` : '',
            otMembers > 0 ? `${otMembers} OT` : '',
          ].filter(Boolean).join(' · ') || 'All clear'}
          delta={pendingActions > 0 ? 'Needs review' : undefined}
          deltaType={pendingActions > 0 ? 'warn' : undefined}
        />
      </div>

      {/* Tab navigation */}
      <div style={{
        display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2,
        marginBottom: 16, width: 'fit-content',
      }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '6px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
              fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
              background: tab === t.key ? palette.g800 : 'transparent',
              color: tab === t.key ? palette.white : palette.g500,
              transition: `all ${transition.fast}`,
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Tab content card */}
      <div style={{
        background: palette.white, borderRadius: admin.cardRadius,
        border: `1px solid ${admin.border}`, padding: '20px', boxShadow: shadow.card,
      }}>
        {tab === 'roster' && <TeamRoster members={teamMembers} onEditMember={setEditMember} onRefresh={refresh} onToast={(msg, type) => setToast({ msg, type })} />}
        {tab === 'payroll' && business && (
          <HoursPayroll
            businessId={business.id}
            otThreshold={otThreshold}
            otWarningThreshold={otWarningThreshold}
          />
        )}
        {tab === 'certifications' && <CertificationsTab certs={certAlerts} />}
        {tab === 'leave' && <LeaveRequestsTab requests={leaveRequests} onRefresh={refresh} />}
        {tab === 'commissions' && showCommissions && business && user && (
          <CommissionsTab
            businessId={business.id}
            userId={user.id}
            operators={teamMembers.filter(m => m.employmentType === 'operator' && m.status === 'active')}
          />
        )}
        {tab === 'invite_codes' && <TeamInvitesTab businessId={business?.id} onToast={(msg, type) => setToast({ msg, type })} />}
        {tab === 'activity' && <ActivityLogTab businessId={business?.id} users={teamMembers.map(m => ({ id: m.id, first_name: m.firstName, last_name: m.lastName, role: m.role, employment_type: m.employmentType }))} />}
      </div>

      <AddToTeamPanel isOpen={memberPanel} onClose={() => setMemberPanel(false)} onCreated={() => { refresh(); setToast({ msg: 'Team member added', type: 'success' }); }} teamTotal={activeCount} />
      <EditTeamMemberPanel member={editMember} onClose={() => setEditMember(null)} onUpdated={() => { refresh(); setToast({ msg: 'Team member updated', type: 'success' }); }} />
      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
